import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Field } from '../components/Field';
import { Spinner } from '../components/Spinner';

// Optional banner state passed from elsewhere via navigate('/sign-in', { state: { banner } })
// — used to surface "Account deleted." after a delete flow, or "You must be 18..." if the
// server rejected an under-18 DOB and nuked the account.
type SignInLocationState = { banner?: string };

// Conservative format gate: local@domain.tld, no whitespace either side of the
// @. Deliberately not stricter — this exists to stop obviously-malformed input
// from reaching Supabase Auth (and generating a bounce), not to validate that
// an address is deliverable.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Email magic link / OTP. We default to magic link (`signInWithOtp` with
// emailRedirectTo) because it's frictionless on desktop. The redirect drops the
// user back into this same SPA with a session attached; useSession picks it up
// and the BootGate routes them onward.
export function SignIn() {
  const location = useLocation();
  const banner = (location.state as SignInLocationState | null)?.banner;
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });
      if (err) throw err;
      setSent(true);
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : 'Failed to send link');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="shell welcome" onSubmit={onSubmit}>
      <header className="welcome__hero rise">
        <div className="brand"><span className="brand__dot" />RESLT</div>
        <h1 className="welcome__title">No guessing.<br /><em>Just progress.</em></h1>
        <p className="welcome__sub">
          Sign in with your email — we'll send you a one-tap link. No password to forget.
        </p>
      </header>

      {banner ? (
        <div className="card rise" style={{ marginBottom: 16 }}>
          <p className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>{banner}</p>
        </div>
      ) : null}

      {sent ? (
        <div className="card card--elevated rise">
          <p className="eyebrow">Check your inbox</p>
          <p className="h3" style={{ marginTop: 4 }}>We just sent a link to {email}</p>
          <p className="muted" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>
            Click it to sign in. The tab you opened it from will pick up your session
            automatically — you can close this tab if you'd rather.
          </p>
        </div>
      ) : (
        <section className="section rise">
          <Field label="Email">
            <input
              className="input"
              type="email"
              required
              autoFocus
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          {error ? <div className="errorbar">{error}</div> : null}
          <button
            type="submit"
            className="btn btn--primary btn--lg btn--full"
            disabled={submitting || email.trim().length < 4}
          >
            {submitting ? (<><Spinner /> Sending…</>) : 'Send sign-in link'}
          </button>
          <p className="faint" style={{ fontSize: 12, textAlign: 'center', marginTop: 4 }}>
            By continuing you agree we'll only use your email to coach you, and you accept the{' '}
            <Link to="/terms" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
              Terms of Service
            </Link>.
          </p>
        </section>
      )}
    </form>
  );
}
